import { useEffect, useRef } from 'react'
import { useLocation } from '@tanstack/react-router'
import type { ActivityEventInput } from '@igf-minar/shared'
import { api, getSessionId } from './api'

const FLUSH_INTERVAL_MS = 10_000
const HEARTBEAT_INTERVAL_MS = 30_000
const MAX_QUEUE = 25
const MAX_LABEL = 100

/**
 * Records the current user's interactions (page views, clicks, heartbeats) and
 * batches them to POST /api/activity. Mounted once in the root layout and only
 * active while the user is authenticated (and past any forced password change).
 */
export function useActivityTracker(enabled: boolean) {
  const location = useLocation()
  const queue = useRef<ActivityEventInput[]>([])

  // Keep the queue + flush logic stable across renders.
  const flush = useRef((keepalive = false) => {
    if (queue.current.length === 0) return
    const batch = queue.current
    queue.current = []
    api.activity.track(getSessionId(), batch, keepalive)
  })

  const push = useRef((event: ActivityEventInput) => {
    queue.current.push(event)
    if (queue.current.length >= MAX_QUEUE) flush.current()
  })

  // Page views: fire on every resolved route change while enabled.
  useEffect(() => {
    if (!enabled) return
    push.current({ type: 'page_view', path: location.pathname })
  }, [enabled, location.pathname])

  // Click tracking, heartbeats, periodic + unload flushing.
  useEffect(() => {
    if (!enabled) return

    function onClick(e: MouseEvent) {
      const target = e.target as Element | null
      const el = target?.closest('button, a, [role="button"], [data-activity]') as HTMLElement | null
      if (!el) return
      const label =
        el.getAttribute('data-activity') ||
        el.getAttribute('aria-label') ||
        el.textContent?.trim() ||
        el.tagName.toLowerCase()
      push.current({
        type: 'click',
        path: window.location.pathname,
        label: label.slice(0, MAX_LABEL),
      })
    }

    function onHidden() {
      if (document.visibilityState === 'hidden') flush.current(true)
    }

    function onPageHide() {
      flush.current(true)
    }

    document.addEventListener('click', onClick, { capture: true })
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', onPageHide)

    const flushTimer = window.setInterval(() => flush.current(), FLUSH_INTERVAL_MS)
    const heartbeatTimer = window.setInterval(() => {
      push.current({ type: 'heartbeat' })
      flush.current()
    }, HEARTBEAT_INTERVAL_MS)

    return () => {
      document.removeEventListener('click', onClick, { capture: true })
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', onPageHide)
      window.clearInterval(flushTimer)
      window.clearInterval(heartbeatTimer)
      flush.current(true)
    }
  }, [enabled])
}
